<template>
    <!-- Below md the form stacks one field per row; from md up the layout
         keeps its original half-width column with paired fields. -->
    <div v-if="allOrganizations" class="gap-y-lg col-span-full mb-20 grid grid-cols-1 md:grid-cols-4">
        <div class="gap-sm col-span-full flex w-full items-center">
            <button class="btn-link flex justify-center" @click="router.back()">
                <i class="i-arrow-left size-6" />
            </button>
            <h1>Create a new user</h1>
        </div>

        <div class="gap-x-sm col-span-full md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-y-1">
            <h2 class="text-md col-span-full md:col-span-2 h-8">User info</h2>
            <FormField label-id="first-name" label="First name" :required="true" :error-message="errors.first_name">
                <input
                    id="first-name"
                    v-model="values.first_name"
                    :autofocus="false"
                    placeholder="Enter first name"
                    type="text"
                />
            </FormField>
            <FormField label-id="last-name" label="Last name" :required="true" :error-message="errors.last_name">
                <input
                    id="org-name"
                    v-model="values.last_name"
                    :autofocus="false"
                    placeholder="Enter last name"
                    type="text"
                />
            </FormField>
            <FormField label-id="email-address" label="Email address" :required="true" :error-message="errors.email">
                <input
                    id="email-address"
                    v-model="values.email"
                    placeholder="example@atnorth.com"
                    type="email"
                    @input="lowercaseEmail"
                />
            </FormField>
            <FormField
                label-id="phone-number"
                label="Phone number"
                :required="false"
                :error-message="errors.phone_number"
            >
                <IntlTelInput
                    :options="{ initialCountry: 'is' }"
                    @change-number="setPhoneNumber"
                    @change-validity="setPhoneNumberValidity"
                />
            </FormField>
            <div class="input-group">
                <InputSelect
                    :label="'System role'"
                    :unique-key="1"
                    :placeholder="'Select system role'"
                    :options="[
                        { value: 'admins', label: 'Admin' },
                        { value: 'lms-admins', label: 'LMS admin' },
                        { value: 'noc-admins', label: 'NOC admin' },
                        { value: 'cdim-admins', label: 'CDIM admin' },
                        { value: 'employees', label: 'Employee' },
                        { value: 'customers', label: 'Customer' },
                        { value: 'contractors', label: 'Contractor' },
                    ]"
                    :value="systemRoleOption"
                    required
                    @update="setSystemRoleOption"
                />
                <div class="flex h-6 items-center">
                    <p v-if="errors.groups" class="error">{{ errors.groups }}</p>
                </div>
            </div>

            <!-- add toggle for non-org user -->
            <div class="input-group gap-sm col-span-full md:col-span-2 flex items-center justify-end">
                <label for="use-organizations" class="!mb-0">Select organizations</label>
                <input
                    id="use-organizations"
                    v-model="useOrganizations"
                    type="checkbox"
                    class="focus:border-(--border-focus) focus:ring-2 focus:ring-(--border-focus) focus:outline-hidden"
                    @click="toggleUseOrganizations"
                    @keydown.enter="toggleUseOrganizations"
                />
            </div>
            <template v-if="useOrganizations">
                <h2 class="text-md mt-sm mb-sm col-span-full md:col-span-2">Add to organization</h2>
                <div class="col-span-full md:col-span-2 mb-10 min-h-48">
                    <div class="flex items-center justify-between gap-4">
                        <div class="input-wrapper w-1/2">
                            <input v-model="searchString" type="text" placeholder="Search" />
                        </div>
                        <p v-if="errors.organizations" class="error">
                            {{ errors.organizations }}
                        </p>
                    </div>
                    <ul class="mt-lg flex min-h-16 flex-col gap-y-1">
                        <li
                            v-for="(org, i) in availableOrganizations"
                            :key="org.organization_id"
                            class="flex min-h-16 items-start justify-between"
                        >
                            <div class="gap-sm flex items-center pt-3">
                                <input
                                    :id="'org-option-' + org.organization_id"
                                    type="checkbox"
                                    :value="selectedOrgIds.some((o) => org.organization_id === o.org_id)"
                                    :checked="selectedOrgIds.some((o) => org.organization_id === o.org_id)"
                                    class="focus:border-(--border-focus) focus:ring-2 focus:ring-(--border-focus) focus:outline-hidden"
                                    @click="toggleOrg(org)"
                                    @keydown.enter="toggleOrg(org)"
                                />
                                <label class="!mb-0" :for="'org-option-' + org.organization_id">{{ org.name }}</label>
                            </div>

                            <div v-if="selectedOrgIds.find((item) => item.org_id === org.organization_id)" class="w-64">
                                <div class="input-group">
                                    <InputSelect
                                        label=""
                                        :unique-key="i + 2"
                                        :placeholder="'Select organization role'"
                                        :options="availableOrgRoles"
                                        :value="
                                            selectedOrgIds.find((item) => item.org_id === org.organization_id)!.orgRole
                                        "
                                        @update="setOrgRole(org.organization_id, $event)"
                                    />
                                    <div class="flex h-6 items-center">
                                        <p v-if="getOrgRoleError(org.organization_id)" class="error">
                                            {{ getOrgRoleError(org.organization_id) }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </template>
            <div v-else class="h-48"></div>
        </div>
    </div>

    <BottomButtons arrow :handle-submit="createUser" full-width @cancel="router.back()" />
</template>

<script lang="ts">
import z from 'zod';

export const AdminUserCreationBodySchema = z.object({
    username: z.string().nonempty(),
    email: z.string().nonempty(),
    first_name: z.string().nonempty(),
    last_name: z.string().nonempty(),
    phone_number: z.string().optional(),
    groups: z.array(z.string()).min(1, 'Required'),
    organizations: z.object({ organization_id: z.number(), organization_role_id: z.string() }, 'Required').array(),
});

export type AdminUserCreationBody = z.infer<typeof AdminUserCreationBodySchema>;
</script>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import router from '@/router/admin-router';
import InputSelect from '@/components/support/input-select.vue';

import { type AdminOrganizationData, type Option } from '@/stores/store-interfaces';
import { useAdminStore } from '@/stores/admin';
import BottomButtons from '@/components/bottom-buttons.vue';
// @ts-expect-error intl-tel-input ships no types for the vueWithUtils entry
import IntlTelInput from 'intl-tel-input/vueWithUtils';
import 'intl-tel-input/styles';
import { FormField } from '@/components/ui/form-field';
import { useFormValidation } from '@/composables/use-form-validation';

const adminStore = useAdminStore();

const validPhoneNumber = ref<boolean>(false);

const setPhoneNumberValidity = (value: boolean) => {
    validPhoneNumber.value = value;
};

const UserCreationSchema = computed(() =>
    AdminUserCreationBodySchema.refine(
        ({ phone_number }) => {
            if (phone_number === undefined || phone_number === null || phone_number === '') {
                return true;
            } else {
                return validPhoneNumber.value;
            }
        },
        { error: 'Invalid phone number', path: ['phone_number'] },
    )
        .refine(
            ({ organizations }) => {
                if (Array.isArray(organizations)) {
                    if (useOrganizations.value) {
                        return organizations.length >= 1;
                    } else {
                        return true;
                    }
                }
            },
            { error: 'Please select at least one organization', path: ['organizations'] },
        )
        .refine(
            ({ organizations }) => {
                // Validate that each organization has a role selected
                if (!useOrganizations.value || !Array.isArray(organizations)) {
                    return true;
                }
                return organizations.every((org) => {
                    return org.organization_role_id && org.organization_role_id !== '';
                });
            },
            { error: 'Required', path: ['organizations'] },
        )
        .refine(
            ({ email }) => {
                const result = z.email().safeParse(email);
                return result.success;
            },
            { error: 'Invalid email', path: ['email'] },
        ),
);

const systemRoleOption = ref<Option[]>([]);

const setSystemRoleOption = (value: Option[]) => {
    systemRoleOption.value = value;
};

const selectedOrgIds = ref<{ org_id: number; orgRole: Option[] }[]>([]);
const searchString = ref<string>('');

const useOrganizations = ref<boolean>(false);

const toggleUseOrganizations = () => {
    useOrganizations.value = !useOrganizations.value;
};
const allOrganizations = ref<AdminOrganizationData[]>([]);
const availableOrgRoles = ref<Option[]>([]);
const availableOrganizations = computed(() =>
    allOrganizations.value.filter((item) =>
        Object.values(item).some((value) =>
            (value?.toString().toLowerCase() ?? '').includes(searchString.value.toLowerCase()),
        ),
    ),
);

const initialValues: AdminUserCreationBody = {
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    groups: [],
    organizations: [],
};

const { values, errors, validate } = useFormValidation(UserCreationSchema, initialValues);

const lowercaseEmail = () => {
    values.email = values.email.toLowerCase();
};

const setPhoneNumber = (value: string) => {
    values.phone_number = value;
};

watch(systemRoleOption, () => (values.groups = systemRoleOption.value.map((role) => role.value)));

watch(useOrganizations, () => {
    if (!useOrganizations.value) {
        values.organizations = [];
    }
});

const toggleOrg = (org: AdminOrganizationData) => {
    if (selectedOrgIds.value.find((item) => item.org_id === org.organization_id)) {
        selectedOrgIds.value = selectedOrgIds.value.filter((item) => item.org_id !== org.organization_id);
    } else {
        selectedOrgIds.value.push({
            org_id: org.organization_id,
            orgRole: [{ value: 1, label: 'org-user' }],
        });
    }
};

const setOrgRole = (orgId: number, orgRole: Option[]) => {
    selectedOrgIds.value.find((item) => item.org_id === orgId)!.orgRole = orgRole;
};

watch(
    selectedOrgIds,
    () => {
        values.organizations = selectedOrgIds.value.flatMap((item) => ({
            organization_id: item.org_id,
            organization_role_id: item.orgRole?.[0]?.value?.toString() || '',
        }));
    },
    { deep: true },
);

watch(values, () => (values.username = values.email));

const getOrgRoleError = (orgId: number) => {
    // Only show errors after validation has been called
    if (!Object.values(errors).some((error) => error !== '')) return '';

    // Check if this specific organization is missing a role
    const orgData = values.organizations.find((org) => org.organization_id === orgId);
    if (!orgData) return '';

    const hasRole = orgData.organization_role_id && orgData.organization_role_id !== '';
    return hasRole ? '' : 'Required';
};

onMounted(async () => {
    allOrganizations.value = await adminStore.getOrganizationsByStatus(1);
    availableOrgRoles.value = await adminStore
        .getAllRoles()
        .then((roles) =>
            roles
                .filter((role) => ['org-user', 'org-admin'].includes(role.name))
                .map((role) => ({ value: role.organization_role_id, label: role.name })),
        );
});

const createUser = async () => {
    const validForm = validate();
    if (!validForm) {
        return;
    }

    const response = await adminStore.createUser(values);
    if (response.success) {
        adminStore.setToastMessage('User created', 'check');
        setTimeout(() => {
            router.back();
        }, 1000);
    } else {
        adminStore.setToastMessage('Failed to create user, please try again', 'error');
    }
};
</script>

<style scoped>
@reference '@/assets/styles/default.css';

label {
    display: flex;
    align-items: center;
    margin-bottom: var(--spacing-xs);
}

.required::before {
    content: '*';
    margin-right: var(--spacing-xs);
    color: var(--color-error);
    display: flex;
    align-items: center;
}
</style>
